import App, { AppInitialProps } from 'next/app';
import Head from 'next/head';
import type { AppProps as NextAppProps, AppContext } from 'next/app';
import { Toast } from '../components/Toolkit/Toast/Toast';
import Router, { useRouter } from 'next/router';
import { DidomiSDK } from '@didomi/react';
import {
  HydrationBoundary,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  type DehydratedState,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import StatusCode from 'status-code-enum';

import Script from 'next/script';
import React, { useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { DefaultSeo, LocalBusinessJsonLd } from 'next-seo';
import getConfig from 'next/config';
import '../helpers/Grid/Grid.scss';
import { SessionProvider } from 'next-auth/react';

import { getServerCookie } from '../utils/CookieUtils';
import type { SectionView } from '../types';
import { GlobalStyles } from '../helpers/GlobalStylesHelper';
import { theme } from '../helpers/Theme/';
import { UserProvider } from '../contexts/UserContext';
import { GlobalProvider } from '../contexts/GlobalContext';
import { getTLD } from '../helpers/domain';
import { randomStringGenerator } from '../helpers/randomStringGenerator';
import 'react-toastify/dist/ReactToastify.css';
import { pushToDataLayer } from '../services/gtmService';
import { Session } from 'next-auth';
import { PAGE } from '../helpers/pages';
import RefreshTokenHandler from '../components/_App/RefreshAccessTokenHandler';
import { useDidomi } from '../hooks/UseDidomi';
import { useRaygun } from '../hooks/UseRaygun';
import { PURPOSES } from '../types/didomi';
import { getDidomiConsents } from '../utils/GetDidomiConsents';
import { useOnNetworkConnectionStatusChange } from '../hooks/UseOnNetworkConnectionStatusChange';
import { AppConsumer } from '../components/_App/_App';
import { ErrorBoundary } from '@dist-marketplace-frontend/components';
import { DefaultLayout } from '../components/Layouts';
import { ErrorPage } from '../components/ErrorPages/ErrorPage';
import { Navigation } from '../domains/Navigation';
import { frontendServerGateway } from '../api/api-utils/commonApiUtils';
import { formatCookie, getDomainFromBaseURL } from '../utils/CookieUtils';
import { COOKIE, MAXIMUM_COOKIE_AGE } from '../helpers/constants';
import { fireToast } from '../helpers/Toasts';
import { isAxiosError } from 'axios';
import { ConvertProvider } from '../contexts/ConvertContext';
import { logger } from '@dist-marketplace-frontend/services';
import { VerticalProvider } from '../contexts/VerticalContext';
import {
  getVerticalClient,
  getVerticalServer,
  VERTICAL,
} from '../helpers/verticals';
import { PrismicPreview } from '@prismicio/next/pages';
import { UserProfile, userSchema } from '../api/schemas/user-profile.schema';

// Fixed from Next v12.3.0 onwards
/** *NOTE*
 * The props returned to the client side component of _app are a combination of `MyApp.getInitialProps`
 * below as well as `getServerSideProps` returned from specific pages. For this reason props with
 * the same key can potentially overwrite each other leading to issues.
 */
type AppProps<P = PageProps> = {
  pageProps: P;
} & Omit<NextAppProps<P>, 'pageProps'>;

type PageProps = {
  userProfile: UserProfile | undefined;
  session?: Session;
  domain: string;
  analyticsConsent: boolean;
  navigation: Navigation[];
  initialFunctionalConsent: boolean;
  vertical: VERTICAL;
  showPricingIndicator?: boolean;
  pricingIndicatorVariationKey?: string | null;
  dehydratedState?: DehydratedState;
};

if (typeof window !== 'undefined' && !window.IntersectionObserver) {
  require('intersection-observer');
}
Router.events.on('routeChangeComplete', (url: string, { shallow }) => {
  if (!shallow) {
    pushToDataLayer({ url, event: 'page_view' });
  }
});

function MyApp({ Component, pageProps }: AppProps<PageProps>) {
  const {
    publicRuntimeConfig: {
      CDN_STATIC_ASSETS,
      GOOGLE_RECAPTCHA,
      DIDOMI_API_KEY,
      DIDOMI_NOTICE_ID,
      RAYGUN_API_KEY,
      INTEGRATION_TESTING,
      ENABLE_SYNC_CONVERT_SCRIPT,
      PRISMIC_REPOSITORY_NAME,
    },
  } = getConfig();
  const {
    domain,
    userProfile,
    session,
    navigation,
    initialFunctionalConsent,
    vertical,
    showPricingIndicator = false,
    pricingIndicatorVariationKey = null,
    dehydratedState,
  } = pageProps;
  const {
    didomiConfig,
    onReady,
    onConsentChanged,
    functionalConsent,
    allowRaygun,
    allowGoogle,
    allowWootric,
    allowBraze,
    allowConvert,
  } = useDidomi({ initialFunctionalConsent });
  const { debugMode, enablePulse } = useRaygun(allowRaygun, 2);
  const { pathname } = useRouter();

  const [
    timeInSecondsBeforeRefreshingSession,
    setTimeInSecondsBeforeRefreshingSession,
  ] = useState(0);

  useOnNetworkConnectionStatusChange();

  const initRaygunScript = () => {
    return {
      __html: `
        !function(a,b,c,d,e,f,g,h){a.RaygunObject=e,a[e]=a[e]||function(){
        (a[e].o=a[e].o||[]).push(arguments)},f=b.createElement(c),g=b.getElementsByTagName(c)[0],
        f.async=1,f.crossorigin='anonymous',f.src=d,g.parentNode.insertBefore(f,g),h=a.onerror,a.onerror=function(b,c,d,f,g){
        h&&h(b,c,d,f,g),g||(g=new Error(b)),a[e].q=a[e].q||[],a[e].q.push({
        e:g})}}(window,document,"script","//cdn.raygun.io/raygun4js/raygun.min.js","rg4js");

        rg4js('apiKey', '${RAYGUN_API_KEY}');
        rg4js('enableCrashReporting', true);
        rg4js('enablePulse', ${enablePulse});

        rg4js('options', {
          debugMode: ${debugMode},
          ignore3rdPartyErrors: true
        });
                
        rg4js('whitelistCrossOriginDomains', ['statics.donedeal.ie', 'statics.donedealtest.com', 'google.com', 'donedeal.ie', 'donedealtest.com', 'donedeal-frontend-pr-*.dsch.build', 'donedeal', 'donedeal.construction']);
                
         rg4js('onBeforeSend', function (payload) {
            let updatedPayload = payload;
            if (payload.Details?.Error?.Message == 'Script error.') {
              const googleAdsUrls = ["doubleclick.net", "googlesyndication.com", "adsensecustomsearchads.com"];
              if (!!payload.Details.Error.StackTrace) {
                if (payload.Details.Error.StackTrace.some(frame => googleAdsUrls.some(url => frame.FileName.includes(url)))) {
                  updatedPayload.Details.Error.Message = "An error occurred while loading an ad.";
                }
              }
            }

            if (payload.Details?.Error?.Message.includes('Loading chunk')) {
              updatedPayload.Details.Error.Message = "Loading chunk error";
            }
            return updatedPayload;
        });
        `,
    };
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            const noToast = query.meta?.noToast;
            if (!noToast) {
              fireToast({
                text:
                  query.meta?.toastErrorMessage ||
                  'Oops! Something went wrong, please try again later',
                type: 'ERROR',
              });
            }
            logger.error({
              tags: query?.meta?.tags,
              error,
              message: query?.meta?.errorMessage ?? 'Network Error',
            });
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            /**
             * A 422 response is valid and generally should be handled at the point of calling.
             * We do not necessarily want to fire an error toast or log to raygun.
             */
            if (
              isAxiosError(error) &&
              error.response?.status ===
                StatusCode.ClientErrorUnprocessableEntity
            ) {
              return;
            }
            const noToast = mutation.meta?.noToast;
            if (!noToast) {
              fireToast({
                text:
                  mutation.meta?.toastErrorMessage ||
                  'Oops! Something went wrong, please try again later.',
                type: 'ERROR',
              });
            }
            logger.error({
              tags: mutation?.meta?.tags,
              error,
              message: mutation?.meta?.errorMessage ?? 'Network Error',
            });
          },
          onSuccess: (_, __, ___, mutation) => {
            if (mutation.meta?.successMessage) {
              fireToast({
                text: mutation.meta.successMessage,
                type: 'SUCCESS',
              });
            }
          },
        }),
      }),
  );

  const breadcrumbs = useMemo(
    () =>
      'breadcrumbs' in pageProps
        ? (pageProps.breadcrumbs as SectionView[])
        : [],
    [pageProps],
  );

  const SEO_ATTRIBUTES = {
    title: "DoneDeal | Ireland's #1 site for Cars & Marketplace",
    description:
      "Ireland's favourite place to buy and sell. DoneDeal brings trusted cars, farming and marketplace together - Ireland's widest selection in one place.",
  };

  return (
    <VerticalProvider
      breadcrumbs={breadcrumbs}
      domain={domain}
      initialVertical={vertical}
    >
      {/* The refetch interval is set to 0 meaning that the session will not be re-fetched automatically in SessionProvider */}
      {/* However we manually refresh the session when it expires in the RefreshTokenHandler component */}
      <SessionProvider
        session={session}
        refetchInterval={timeInSecondsBeforeRefreshingSession}
        refetchOnWindowFocus={false}
        refetchWhenOffline={false}
      >
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={dehydratedState}>
            <ThemeProvider theme={theme}>
              <GlobalProvider navigation={navigation}>
                <UserProvider
                  initialUser={userProfile}
                  consentOptions={{
                    functionalConsent,
                    allowGoogle,
                    allowWootric,
                    allowBraze,
                    allowConvert,
                  }}
                >
                  <ConvertProvider
                    showPricingIndicator={showPricingIndicator}
                    pricingIndicatorVariationKey={pricingIndicatorVariationKey}
                  >
                    <DefaultSeo
                      openGraph={{
                        type: 'website',
                        locale: 'en_IE',
                        url: domain,
                        site_name: 'DoneDeal.ie',
                        images: [
                          {
                            url: `${CDN_STATIC_ASSETS}/images/meta/social-d-placeholder.jpg`,
                            width: 2000,
                            height: 1000,
                          },
                        ],
                        ...SEO_ATTRIBUTES,
                      }}
                      twitter={{
                        site: '@DoneDeal',
                        cardType: 'app',
                      }}
                      {...SEO_ATTRIBUTES}
                    />

                    <Head>
                      <meta
                        name="viewport"
                        content="initial-scale=1.0, width=device-width"
                      />
                      <meta
                        name="apple-itunes-app"
                        content="app-id=428330596"
                      />
                      <meta name="google" content="nositelinkssearchbox" />
                      <meta property="fb:app_id" content="553023201403588" />
                      <meta
                        name="facebook-domain-verification"
                        content="mkpjnrsx4etl2yzu4hv19ie94j808l"
                      />
                      <link
                        rel="icon"
                        href={`${CDN_STATIC_ASSETS}/favicons/favicon.svg`}
                      />
                      <link
                        rel="shortcut icon"
                        href={`${CDN_STATIC_ASSETS}/favicons/favicon.svg`}
                      ></link>

                      {/* Fallback  */}
                      <link
                        rel="shortcut icon"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-48x48.png`}
                      ></link>

                      <link
                        rel="mask-icon"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-48x48.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="57x57"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-57x57.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="60x60"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-60x60.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="72x72"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-72x72.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="76x76"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-76x76.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="114x114"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-114x114.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="120x120"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-120x120.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="144x144"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-144x144.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="152x152"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-152x152.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="180x180"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-180x180.png`}
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="144x144"
                        href={`${CDN_STATIC_ASSETS}/favicons/favicon.svg`}
                      />
                      <link
                        rel="icon"
                        type="image/png"
                        sizes="48x48"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-48x48.png`}
                      />
                      <link
                        rel="icon"
                        type="image/png"
                        sizes="96x96"
                        href={`${CDN_STATIC_ASSETS}/favicons/donedeal-favicon-96x96.png`}
                      />
                      <link
                        rel="icon"
                        type="image/png"
                        sizes="192x192"
                        href={`${CDN_STATIC_ASSETS}/favicons/android-icon-192x192.png`}
                      />
                    </Head>
                    {/* Exclude the slice-simulator route: it's a Prismic tool that renders
                      slices in isolation, and the cookie banner interferes with the preview */}
                    {!INTEGRATION_TESTING &&
                      pathname !== '/slice-simulator' && (
                        <DidomiSDK
                          apiKey={DIDOMI_API_KEY}
                          noticeId={DIDOMI_NOTICE_ID}
                          iabVersion={2}
                          gdprAppliesGlobally
                          config={didomiConfig}
                          onReady={onReady}
                          onConsentChanged={onConsentChanged}
                        />
                      )}
                    <script
                      src={`https://www.google.com/recaptcha/enterprise.js?render=${GOOGLE_RECAPTCHA}`}
                      async
                    />
                    <Script
                      id="raygun"
                      dangerouslySetInnerHTML={initRaygunScript()}
                      crossOrigin={'anonymous'}
                    />

                    {!ENABLE_SYNC_CONVERT_SCRIPT && allowConvert && (
                      <Script
                        async
                        type="text/javascript"
                        src="//cdn-4.convertexperiments.com/js/10042404-10043067.js"
                        crossOrigin="anonymous"
                      />
                    )}

                    <LocalBusinessJsonLd
                      type="Organization"
                      id={domain}
                      name="DoneDeal.ie"
                      description="Buy & Sell on Ireland's Largest Motor & Classifieds Marketplace, where more than 3,000,000 daily visitors come to Discover New & Used Cars, Other Motor, Farming and day to day items."
                      url={domain}
                      address={{
                        streetAddress: 'DoneDeal.ie, Crescent Quay',
                        addressLocality: 'Wexford',
                        addressRegion: 'Wexford',
                        postalCode: 'Y35HE67',
                        addressCountry: 'IE',
                      }}
                      sameAs={[
                        'https://www.facebook.com/DoneDealIreland',
                        'https://twitter.com/donedeal',
                        'https://www.blog.donedeal.ie',
                        'https://www.youtube.com/user/DoneDealers',
                      ]}
                    />
                    <GlobalStyles cdnUrl={CDN_STATIC_ASSETS} />
                    <ErrorBoundary
                      fallback={
                        <DefaultLayout useGenericLogoInHeader domain={domain}>
                          <ErrorPage cdnUrl={CDN_STATIC_ASSETS} />
                        </DefaultLayout>
                      }
                    >
                      <Component {...pageProps} domain={domain} />
                    </ErrorBoundary>
                    <AppConsumer domain={domain} />
                    <RefreshTokenHandler
                      setTimeInSecondsBeforeRefreshingSession={
                        setTimeInSecondsBeforeRefreshingSession
                      }
                    />
                    <Toast />
                    {/* Devtools is automatically stripped out in a production build */}
                    <ReactQueryDevtools initialIsOpen={false} />
                    {/* Exclude the slice-simulator route: PrismicPreview polls
                      updatePreviewURL continuously which causes an infinite
                      request loop in the simulator */}
                    {pathname !== '/slice-simulator' && (
                      <PrismicPreview
                        repositoryName={PRISMIC_REPOSITORY_NAME}
                        // Must match the Prismic preview and exit-preview API paths
                        updatePreviewURL="/api/prismic/preview"
                        exitPreviewURL="/api/prismic/exit-preview"
                      />
                    )}
                  </ConvertProvider>
                </UserProvider>
              </GlobalProvider>
            </ThemeProvider>
          </HydrationBoundary>
        </QueryClientProvider>
      </SessionProvider>
    </VerticalProvider>
  );
}
/**
 * By adding getInitialProps here we are opting the entire app out of automatic static optimization.
 * For now this is acceptable, we will revisit this when the need arises.
 */
MyApp.getInitialProps = async (
  appContext: AppContext,
): Promise<AppInitialProps> => {
  const {
    publicRuntimeConfig: { BASE_URL },
  } = getConfig();
  let pageProps: AppInitialProps;
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  pageProps = await App.getInitialProps(appContext);
  const tld = getTLD(appContext.ctx.req);
  const domain = `${BASE_URL}${tld}`;

  const vertical: VERTICAL =
    typeof appContext.ctx.req !== 'undefined'
      ? getVerticalServer(appContext.ctx)
      : getVerticalClient();

  const didomiToken: string | undefined = getServerCookie(
    'didomi_token',
    appContext.ctx,
    true,
  );

  let navigation: Navigation[] = [];

  const host = domain.split('//');
  const navigationDomain = host.length > 0 && host[1] ? host[1] : null;

  if (navigationDomain) {
    try {
      const params = new URLSearchParams({
        domain: navigationDomain,
      });
      const navigationResponse = await frontendServerGateway.get(
        `/navigation?${params.toString()}`,
      );
      if (navigationResponse.status === 200) {
        navigation = navigationResponse.data;
      }
    } catch (error) {
      logger.error({
        message: 'Error getting navigation data',
        route: PAGE.APP,
        error,
      });
    }
  }

  const consents = didomiToken
    ? getDidomiConsents({
        token: didomiToken,
        onError: (error) =>
          logger.error({
            message: 'Failed to decode Didomi token',
            route: PAGE.APP,
            error,
          }),
      })
    : null;

  const functionalConsent =
    consents?.enabledPurposes?.includes(PURPOSES.FUNCTIONAL) ?? false;

  const sessionID: string | undefined = getServerCookie(
    COOKIE.DD_CLIENT_ID,
    appContext.ctx,
    true,
  );

  if (!sessionID && functionalConsent) {
    const newSessionID = formatCookie(
      COOKIE.DD_CLIENT_ID,
      randomStringGenerator(),
      {
        domain: getDomainFromBaseURL(domain),
        maxAge: MAXIMUM_COOKIE_AGE,
        path: '/',
        httpOnly: false,
      },
    );
    appContext.ctx.res?.setHeader('set-cookie', newSessionID);
  }

  let userProfile: UserProfile | undefined;

  try {
    const { data } = await frontendServerGateway.get('/user-profile', {
      headers: { cookie: appContext.ctx.req?.headers.cookie },
    });
    userProfile = userSchema.parse(data);
  } catch (error: unknown) {
    /**
     * A 401 (ClientErrorUnauthorized) error is returned when no session is found for the user, implying that
     * they are logged-out.
     *
     * We do not log this type of error in order to avoid excessive logging, since this may be triggered every time
     * a logged-out user loads any page on the application, rather than indicating something is wrong.
     */
    const isClientErrorUnauthorized =
      isAxiosError(error) &&
      error.status === StatusCode.ClientErrorUnauthorized;
    if (!isClientErrorUnauthorized) {
      logger.error({
        message: 'Error getting user data',
        route: PAGE.APP,
        error,
      });
    }
  }

  return {
    pageProps: {
      ...pageProps,
      vertical,
      userProfile,
      domain,
      navigation,
      initialFunctionalConsent: functionalConsent || false,
    },
  };
};

export default MyApp;
